import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";
import { useGetSiteSettingsQuery } from "@/src/services/api";

export type HelpButtonPosition =
    | "upper-left"
    | "upper-left-2"
    | "lower-right"
    | "lower-right-2";

export type HomepageSectionKey =
    | "news"
    | "services"
    | "featuredOrgs"
    | "featuredEvents"
    | "featuredProfessionals";

export const HOMEPAGE_SECTION_LABELS: Record<HomepageSectionKey, string> = {
    services: "Product Certification Services",
    news: "News banner with latest industry updates",
    featuredOrgs: "Featured Organizations",
    featuredEvents: "Featured Events",
    featuredProfessionals: "Featured Profiles (Professionals)",
};

export const DEFAULT_HOMEPAGE_ORDER: HomepageSectionKey[] = [
    "news",
    "services",
    "featuredOrgs",
    "featuredEvents",
    "featuredProfessionals",
];

export interface FooterConfig {
    social: { x: boolean; youtube: boolean };
    text: string;
    formVisible: boolean;
}

export const DEFAULT_FOOTER_TEXT =
    "Get your product certified by our team of experts and gain access to exclusive resources and support.";

export const DEFAULT_FOOTER_CONFIG: FooterConfig = {
    social: { x: true, youtube: true },
    text: DEFAULT_FOOTER_TEXT,
    formVisible: true,
};

/** The five directory index pages an admin can restyle. */
export type IndexPageKey =
    | "organizations"
    | "events"
    | "regulations"
    | "articles"
    | "professionals";

export const INDEX_PAGE_KEYS: IndexPageKey[] = [
    "organizations",
    "events",
    "regulations",
    "articles",
    "professionals",
];

/** See the backend entity for what each style looks like. */
export interface IndexLayoutConfig {
    style: 1 | 2 | 3;
    showCreate: boolean;
    showUpload: boolean;
    filterBar: "shared" | "events";
    /** Show the promoted section on this INDEX page (not the homepage). */
    showFeatured: boolean;
}

/** Each page's historical look — matches DEFAULT_INDEX_LAYOUTS server-side. */
export const DEFAULT_INDEX_LAYOUTS: Record<IndexPageKey, IndexLayoutConfig> = {
    organizations: { style: 1, showCreate: true, showUpload: true, filterBar: "shared", showFeatured: true },
    professionals: { style: 1, showCreate: true, showUpload: false, filterBar: "shared", showFeatured: true },
    articles: { style: 2, showCreate: true, showUpload: false, filterBar: "shared", showFeatured: true },
    regulations: { style: 2, showCreate: true, showUpload: false, filterBar: "shared", showFeatured: true },
    events: { style: 3, showCreate: true, showUpload: true, filterBar: "events", showFeatured: true },
};

function normalizeIndexLayouts(
    raw: unknown,
): Record<IndexPageKey, IndexLayoutConfig> {
    const source = (raw ?? {}) as Record<string, Partial<IndexLayoutConfig>>;
    const out = {} as Record<IndexPageKey, IndexLayoutConfig>;
    for (const key of INDEX_PAGE_KEYS) {
        const fallback = DEFAULT_INDEX_LAYOUTS[key];
        const given = source[key] ?? {};
        const style = Number(given.style);
        out[key] = {
            style: style === 1 || style === 2 || style === 3
                ? (style as 1 | 2 | 3)
                : fallback.style,
            showCreate:
                typeof given.showCreate === "boolean"
                    ? given.showCreate
                    : fallback.showCreate,
            showUpload:
                typeof given.showUpload === "boolean"
                    ? given.showUpload
                    : fallback.showUpload,
            filterBar:
                given.filterBar === "events" || given.filterBar === "shared"
                    ? given.filterBar
                    : fallback.filterBar,
            showFeatured:
                typeof given.showFeatured === "boolean"
                    ? given.showFeatured
                    : fallback.showFeatured,
        };
    }
    return out;
}

export interface SiteSettings {
    eventsVisible: boolean;
    regulationsVisible: boolean;
    articlesVisible: boolean;
    professionalsVisible: boolean;
    /** Shows the "Render placeholders" button on the professionals index. */
    professionalsPlaceholdersEnabled: boolean;
    allowGuestSuggestedEdits: boolean;
    searchNavVisible: boolean;
    /** Master switch for the site-wide analytics beacon pipeline. */
    trackGlobalTraffic: boolean;
    /** Advanced tracking: hourly analytics rollups instead of nightly. */
    analyticsAdvancedRollups: boolean;
    helpButtonPosition: HelpButtonPosition;
    homepageSectionOrder: HomepageSectionKey[];
    /** Sections the admin hid from the homepage (subset of the order). */
    homepageHiddenSections: HomepageSectionKey[];
    footerConfig: FooterConfig;
    /** Promotion billing scheme: fixed monthly rate (default) or bids. */
    promotionsPeriodType: "monthly" | "bid";
    /** Monthly mode: first month free for an org's first promotion. */
    promotionsFreeIntro: boolean;
    /** Fixed monthly-mode price per promoted page, in cents. */
    promotionsMonthlyRateCents: number;
    /** Site-wide password gate. The hash is never sent to clients. */
    sitePasswordEnabled: boolean;
    /** Whether a gate password has been set (no value is exposed). */
    sitePasswordSet: boolean;
    regulationsSideNav: boolean;
    articlesSideNav: boolean;
    /** Per-page index layout, always fully populated for all five pages. */
    indexLayouts: Record<IndexPageKey, IndexLayoutConfig>;
}

interface SiteSettingsContextValue {
    settings: SiteSettings;
    loading: boolean;
}

const DEFAULT_SETTINGS: SiteSettings = {
    eventsVisible: true,
    regulationsVisible: true,
    articlesVisible: true,
    professionalsVisible: true,
    professionalsPlaceholdersEnabled: false,
    allowGuestSuggestedEdits: false,
    searchNavVisible: true,
    trackGlobalTraffic: false,
    analyticsAdvancedRollups: false,
    helpButtonPosition: "upper-left",
    homepageSectionOrder: DEFAULT_HOMEPAGE_ORDER,
    homepageHiddenSections: [],
    footerConfig: DEFAULT_FOOTER_CONFIG,
    promotionsPeriodType: "monthly",
    promotionsFreeIntro: true,
    promotionsMonthlyRateCents: 1000,
    // Gate defaults ON: a failed settings fetch must never accidentally
    // expose a site that is meant to be locked.
    sitePasswordEnabled: true,
    sitePasswordSet: false,
    regulationsSideNav: true,
    articlesSideNav: true,
    indexLayouts: DEFAULT_INDEX_LAYOUTS,
};

/** Validate + normalise the homepage order so all sections are always present. */
function normalizeHomepageOrder(raw: unknown): HomepageSectionKey[] {
    const out: HomepageSectionKey[] = [];
    if (Array.isArray(raw)) {
        for (const k of raw) {
            if (
                DEFAULT_HOMEPAGE_ORDER.includes(k as HomepageSectionKey) &&
                !out.includes(k as HomepageSectionKey)
            ) {
                out.push(k as HomepageSectionKey);
            }
        }
    }
    // Append any sections missing from the stored order (e.g. newly added ones).
    for (const k of DEFAULT_HOMEPAGE_ORDER) if (!out.includes(k)) out.push(k);
    return out;
}

function normalizeFooterConfig(raw: any): FooterConfig {
    if (!raw || typeof raw !== "object") return { ...DEFAULT_FOOTER_CONFIG };
    return {
        social: {
            x: raw.social?.x !== false,
            youtube: raw.social?.youtube !== false,
        },
        text:
            typeof raw.text === "string" && raw.text.trim().length > 0
                ? raw.text
                : DEFAULT_FOOTER_TEXT,
        formVisible: raw.formVisible !== false,
    };
}

const CACHE_KEY = "pc_site_settings_v1";

function readCache(): SiteSettings | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        // Re-normalize on read — NEVER trust the cached shape. Caches
        // written by older builds miss later-added fields (a cache from
        // before homepageHiddenSections existed crashed the homepage's
        // section filter for every returning visitor: the render
        // dereferenced undefined before the fresh API response could
        // replace the stale object). parseSettings defaults every field,
        // so any historical or future shape drift degrades to defaults
        // instead of a client-side exception.
        return parseSettings(parsed);
    } catch {
        return null;
    }
}

function writeCache(s: SiteSettings): void {
    if (typeof window === "undefined") return;
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(s));
    } catch {
        // storage quota — ignore
    }
}

/**
 * Normalize ANY settings-shaped input — a fresh API response, or a
 * localStorage cache written by an arbitrarily old build — into a complete
 * SiteSettings. Every field is individually defensive, so missing or
 * malformed values degrade to their defaults. Exported for the cache-shape
 * regression test.
 */
export function parseSettings(data: {
    eventsVisible?: boolean;
    regulationsVisible?: boolean;
    articlesVisible?: boolean;
    professionalsVisible?: boolean;
    professionalsPlaceholdersEnabled?: boolean;
    allowGuestSuggestedEdits?: boolean;
    searchNavVisible?: boolean;
    trackGlobalTraffic?: boolean;
    analyticsAdvancedRollups?: boolean;
    helpButtonPosition?: string;
    homepageSectionOrder?: unknown;
    homepageHiddenSections?: unknown;
    footerConfig?: unknown;
    sitePasswordEnabled?: boolean;
    sitePasswordSet?: boolean;
    regulationsSideNav?: boolean;
    articlesSideNav?: boolean;
    indexLayouts?: unknown;
    promotionsPeriodType?: string;
    promotionsFreeIntro?: boolean;
    promotionsMonthlyRateCents?: number;
}): SiteSettings {
    return {
        eventsVisible: data.eventsVisible !== false,
        regulationsVisible: data.regulationsVisible !== false,
        articlesVisible: data.articlesVisible !== false,
        professionalsVisible: data.professionalsVisible !== false,
        // Opt-in (defaults off), unlike the visible flags above.
        professionalsPlaceholdersEnabled:
            data.professionalsPlaceholdersEnabled === true,
        allowGuestSuggestedEdits: data.allowGuestSuggestedEdits === true,
        searchNavVisible: data.searchNavVisible === true,
        // Opt-in (defaults off) — analytics tracking flags.
        trackGlobalTraffic: data.trackGlobalTraffic === true,
        analyticsAdvancedRollups: data.analyticsAdvancedRollups === true,
        // "original"/"center" are retired layouts: any legacy stored
        // value (and unset) falls back to lower-right.
        helpButtonPosition: ([
            "upper-left",
            "upper-left-2",
            "lower-right",
            "lower-right-2",
        ].includes(data.helpButtonPosition ?? "")
            ? data.helpButtonPosition
            : "lower-right") as HelpButtonPosition,
        homepageSectionOrder: normalizeHomepageOrder(data.homepageSectionOrder),
        homepageHiddenSections: Array.isArray(data.homepageHiddenSections)
            ? (data.homepageHiddenSections.filter((k) =>
                  DEFAULT_HOMEPAGE_ORDER.includes(k as HomepageSectionKey),
              ) as HomepageSectionKey[])
            : [],
        footerConfig: normalizeFooterConfig(data.footerConfig),
        promotionsPeriodType:
            data.promotionsPeriodType === "bid" ? "bid" : "monthly",
        promotionsFreeIntro: data.promotionsFreeIntro !== false,
        promotionsMonthlyRateCents:
            typeof data.promotionsMonthlyRateCents === "number" &&
            Number.isFinite(data.promotionsMonthlyRateCents)
                ? Math.max(0, Math.round(data.promotionsMonthlyRateCents))
                : 1000,
        sitePasswordEnabled: data.sitePasswordEnabled !== false,
        sitePasswordSet: data.sitePasswordSet === true,
        regulationsSideNav: data.regulationsSideNav !== false,
        articlesSideNav: data.articlesSideNav !== false,
        indexLayouts: normalizeIndexLayouts(data.indexLayouts),
    };
}

const SiteSettingsContext = createContext<SiteSettingsContextValue>({
    settings: DEFAULT_SETTINGS,
    loading: true,
});

export function SiteSettingsProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    // Always start with DEFAULT_SETTINGS so server and client render identically
    // on first pass (avoids hydration mismatch). The cache is applied in a
    // useEffect immediately after mount so the correct state is in place before
    // the first paint completes — minimising any visible flash.
    const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
    const [hasCachedData, setHasCachedData] = useState(false);

    // Step 1: apply any locally-cached settings right after mount (before first
    // paint) so the correct nav variant is visible immediately on the client.
    useLayoutEffect(() => {
        const cached = readCache();
        if (cached) {
            setSettings(cached);
            setHasCachedData(true);
        }
    }, []);

    const { data, isLoading } = useGetSiteSettingsQuery(undefined, {
        // Always re-fetch when the context mounts so settings changed by an
        // admin are picked up immediately on the next page visit.
        refetchOnMountOrArgChange: true,
    });

    // Step 2: replace with fresh API data and persist to cache.
    useEffect(() => {
        if (!data) return;
        const fresh = parseSettings(data);
        setSettings(fresh);
        writeCache(fresh);
        setHasCachedData(true);
    }, [data]);

    return (
        <SiteSettingsContext.Provider
            value={{ settings, loading: isLoading && !hasCachedData }}
        >
            {children}
        </SiteSettingsContext.Provider>
    );
}

export function useSiteSettings(): SiteSettingsContextValue {
    return useContext(SiteSettingsContext);
}

/** Height of the standard single-row NavBar (px). */
export const STANDARD_NAV_HEIGHT = 64;

/**
 * Height of the multi-row SearchNavBar (px).
 * Row 1 (utility bar) ≈ 44 px
 * Row 2 (logo + search) ≈ 64 px
 * Row 3 (nav links)    ≈ 49 px
 * Total               ≈ 157 px; 160 adds a 3 px safety buffer.
 */
export const SEARCH_NAV_HEIGHT = 160;

/**
 * Returns the correct top-offset (in px) for page content based on which
 * navigation bar is currently active.  Use this as `paddingTop` / `marginTop`
 * on the first full-width element beneath the fixed AppBar.
 */
export function useNavHeight(): number {
    const { settings } = useSiteSettings();
    return settings.searchNavVisible ? SEARCH_NAV_HEIGHT : STANDARD_NAV_HEIGHT;
}
